import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import fileDownload from "js-file-download";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import DownloadIcon from "@mui/icons-material/Download";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import PeopleIcon from "@mui/icons-material/People";
import LogoutIcon from "@mui/icons-material/Logout";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { AuthContext } from "../context/AuthContext";
import LocalLibraryIcon from "@mui/icons-material/LocalLibrary";
import {
  Button,
  ListItemIcon,
  Tab,
  Tabs,
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
} from "@mui/material";
import { Dashboard } from "@mui/icons-material";
import HostelBookingHistory from "./HostelBookingHistory";
import LibraryBookingHistory from "./LibraryBookingHistory";
import AdminStudentList from "./AdminStudentList";
import toast from "react-hot-toast";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import AdminComplaints from "./AdminComplaints";
import MarkEmailUnreadOutlinedIcon from "@mui/icons-material/MarkEmailUnreadOutlined";
import ContactUs from "./ContactUs";
import StarIcon from "@mui/icons-material/Star";
import AdminReviews from "./AdminReviews";
import RoomLibraryManager from "../components/HostelLibraryManager";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import MonthlyFee from "./MonthlyFee";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import LocalLibraryOutlinedIcon from "@mui/icons-material/LocalLibraryOutlined";
import ArticleIcon from "@mui/icons-material/Article";
import AdminBlogs from "./AdminBlogs";
import AdminSwitchRequests from "./AdminSwitchRequests";
import SwitchRequestIcon from "@mui/icons-material/SwapHoriz";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import DepositManagement from "./DepositManagement";
import RevenueManagement from "./RevenueManagement";
import GlobalStudentSearch from "../components/GlobalStudentSearch";

const AdminDashboard = () => {
  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const [hostelStats, setHostelStats] = useState(null);
  const [libraryStats, setLibraryStats] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(() => {
    return localStorage.getItem("admin_selected_menu") || "Dashboard";
  });
  const [activeTab, setActiveTab] = useState(() => {
    return Number(localStorage.getItem("admin_dashboard_tab") || 0);
  });
  const [isLoading, setIsLoading] = useState(true);
  const [paymentType, setPaymentType] = useState("hostel");
  const [paymentFilter, setPaymentFilter] = useState("today");
  const [paymentMonth, setPaymentMonth] = useState(String(new Date().getMonth() + 1));
  const [paymentYear, setPaymentYear] = useState(String(new Date().getFullYear()));
  const [paymentSearch, setPaymentSearch] = useState("");
  const [paymentReport, setPaymentReport] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);


  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    localStorage.setItem("admin_dashboard_tab", newValue);
  };

  const token = localStorage.getItem("token");
  const refreshToken = localStorage.getItem("refreshToken");
  const config = { headers: { Authorization: `Bearer ${token}` } };

  const refreshAccessToken = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}token/refresh/`,
        {
          refresh: refreshToken,
        }
      );

      const { access, refresh } = response.data;
      localStorage.setItem("token", access);
      localStorage.setItem("refreshToken", refresh);

      return access;
    } catch (error) {
      console.error("Token refresh failed:", error);
      handleLogout();
      throw error;
    }
  };

  const makeAuthenticatedRequest = async (apiCall) => {
    try {
      return await apiCall();
    } catch (error) {
      if (error.response?.status === 401) {
        try {
          const newToken = await refreshAccessToken();
          const newConfig = {
            headers: { Authorization: `Bearer ${newToken}` },
          };

          // Retry the original request with new token
          return await apiCall(newConfig);
        } catch (refreshError) {
          handleLogout();
          throw refreshError;
        }
      }
      throw error;
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const selectAdminMenu = (menuName) => {
    setSelectedTab(menuName);
    localStorage.setItem("admin_selected_menu", menuName);
    setMobileOpen(false);
  };

  const downloadHostelCSV = async () => {
    const loadingToast = toast.loading("📥 Downloading hostel data...");
    try {
      const res = await makeAuthenticatedRequest(async (newConfig = config) => {
        return axios.get(
          `${process.env.REACT_APP_API_URL}hostel/admin/export-csv/`,
          {
            ...newConfig,
            responseType: "blob",
          }
        );
      });

      fileDownload(
        res.data,
        `hostel_bookings_${new Date().toISOString().split("T")[0]}.csv`
      );
      toast.dismiss(loadingToast);
      toast.success("✅ Hostel CSV downloaded successfully!", {
        duration: 4000,
        icon: "📊",
      });
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error("❌ Failed to download Hostel CSV. Please try again.", {
        duration: 5000,
      });
    }
  };

  const downloadLibraryCSV = async () => {
    const loadingToast = toast.loading("Downloading library data...");
    try {
      const res = await makeAuthenticatedRequest(async (newConfig = config) => {
        return axios.get(
          `${process.env.REACT_APP_API_URL}library/admin/export-csv/`,
          {
            ...newConfig,
            responseType: "blob",
          }
        );
      });
      fileDownload(res.data, "library_bookings.csv");
      toast.dismiss(loadingToast);
      toast.success("✅ Library CSV downloaded successfully!");
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error("❌ Failed to download Library CSV.");
    }
  };

  const fetchDashboardData = async () => {
    setIsLoading(true);
    const loadingToast = toast.loading("Loading dashboard data...");
    try {
      const [hostelStatsRes, libraryStatsRes] = await Promise.all([
        makeAuthenticatedRequest(async (newConfig = config) => {
          return axios.get(
            `${process.env.REACT_APP_API_URL}hostel/admin/hostel-dashboard-stats/`,
            newConfig
          );
        }),
        makeAuthenticatedRequest(async (newConfig = config) => {
          return axios.get(
            `${process.env.REACT_APP_API_URL}library/admin/library-dashboard-stats/`,
            newConfig
          );
        }),
      ]);
      toast.dismiss(loadingToast);
      setHostelStats(hostelStatsRes.data);
      setLibraryStats(libraryStatsRes.data);
      setIsLoading(false);
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error("❌ Failed to load dashboard data");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const drawerItems = [
    {
      label: "Dashboard",
      icon: (isSelected) => (
        <Dashboard className={isSelected ? "text-white" : "text-blue-600"} />
      ),
      action: () => {
        selectAdminMenu("Dashboard");
      },
    },
    {
      label: "Hostel History",
      icon: (isSelected) => (
        <ApartmentOutlinedIcon
          className={isSelected ? "text-white" : "text-blue-600"}
        />
      ),
      action: () => {
        selectAdminMenu("Hostel History");
      },
    },
    {
      label: "Library History",
      icon: (isSelected) => (
        <LocalLibraryOutlinedIcon
          className={isSelected ? "text-white" : "text-blue-600"}
        />
      ),
      action: () => {
        selectAdminMenu("Library History");
      },
    },
    {
      label: "View All Students",
      icon: (isSelected) => (
        <PeopleIcon className={isSelected ? "text-white" : "text-blue-600"} />
      ),
      action: () => {
        selectAdminMenu("View All Students");
      },
    },
    {
      label: "Monthly Fee",
      icon: (isSelected) => (
        <CurrencyRupeeIcon
          className={isSelected ? "text-white" : "text-blue-600"}
        />
      ),
      action: () => {
        selectAdminMenu("Monthly Fee");
      },
    },
    {
      label: "Payment Report",
      icon: (isSelected) => (
        <CurrencyRupeeIcon
          className={isSelected ? "text-white" : "text-blue-600"}
        />
      ),
      action: () => {
        selectAdminMenu("Payment Report");
      },
    },
    {
      label: "Revenue",
      icon: (isSelected) => (
        <TrendingUpIcon className={isSelected ? "text-white" : "text-blue-600"} />
      ),
      action: () => {
        selectAdminMenu("Revenue");
      },
    },
    {
      label: "Deposit Management",
      icon: (isSelected) => (
        <AccountBalanceWalletIcon
          className={isSelected ? "text-white" : "text-blue-600"}
        />
      ),
      action: () => {
        selectAdminMenu("Deposit Management");
      },
    },
    {
      label: "Switch Requests",
      icon: (isSelected) => (
        <SwitchRequestIcon
          className={isSelected ? "text-white" : "text-blue-600"}
        />
      ),
      action: () => {
        selectAdminMenu("Switch Requests");
      },
    },
    {
      label: "Blogs",
      icon: (isSelected) => (
        <ArticleIcon className={isSelected ? "text-white" : "text-blue-600"} />
      ),
      action: () => {
        selectAdminMenu("Blogs");
      },
    },
    {
      label: "Complaints",
      icon: (isSelected) => (
        <ReportProblemIcon
          className={isSelected ? "text-white" : "text-blue-600"}
        />
      ),
      action: () => {
        selectAdminMenu("Complaints");
      },
    },
    {
      label: "Contacts",
      icon: (isSelected) => (
        <MarkEmailUnreadOutlinedIcon
          className={isSelected ? "text-white" : "text-blue-600"}
        />
      ),
      action: () => {
        selectAdminMenu("Contacts");
      },
    },
    {
      label: "Downloads",
      icon: (isSelected) => (
        <DownloadIcon className={isSelected ? "text-white" : "text-blue-600"} />
      ),
      action: () => {
        selectAdminMenu("Downloads");
      },
    },
    {
      label: "Reviews",
      icon: (isSelected) => (
        <StarIcon className={isSelected ? "text-white" : "text-blue-600"} />
      ),
      action: () => {
        selectAdminMenu("Reviews");
      },
    },
    {
      label: "Logout",
      icon: (isSelected) => (
        <LogoutIcon className={isSelected ? "text-white" : "text-red-600"} />
      ),
      action: () => {
        handleLogout();
        setMobileOpen(false);
      },
    },
  ];

  const drawerContent = (
    <Box className="h-full overflow-y-auto bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 text-white relative overflow-hidden">
      {/* Background decoration */}
      <Box className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-purple-500" />
      <Box className="absolute top-20 right-0 w-32 h-32 bg-blue-500/10 rounded-full -translate-y-16 translate-x-16" />
      <Box className="absolute bottom-20 left-0 w-24 h-24 bg-purple-500/10 rounded-full translate-y-12 -translate-x-12" />

      <Box className="relative z-10 h-full flex flex-col">
        <Box className="p-6 border-b border-gray-700/50">
          <Box className="flex items-center gap-4">
            <Avatar
              src="/images/logo.png"
              alt="A-1 Ajinkya Hostel"
              className="w-14 h-14 border-2 border-blue-500/30 shadow-lg"
            />
            <Box>
              <Typography variant="h6" className="font-bold text-white mb-1">
                Ajinkya Hostel
              </Typography>
              <Typography
                variant="caption"
                className="text-gray-400 bg-gray-800/50 px-2 py-1 rounded-full"
              >
                Admin Panel
              </Typography>
            </Box>
          </Box>
        </Box>

        <List className="py-4 flex-1">
          {drawerItems.map((item, index) => (
            <ListItem
              button
              key={item.label}
              onClick={item.action}
              className={`mb-1 mx-3 rounded-xl cursor-pointer transition-all duration-300 ${
                selectedTab === item.label
                  ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/25"
                  : "text-gray-300 hover:bg-gray-700/50 hover:text-white"
              }`}
              sx={{
                "&:hover": {
                  transform:
                    selectedTab === item.label ? "none" : "translateX(6px)",
                },
              }}
            >
              <ListItemIcon className="min-w-0 mr-3">
                <Box
                  className={`p-1 rounded-lg ${
                    selectedTab === item.label
                      ? "bg-white/20 text-white"
                      : "bg-transparent"
                  }`}
                >
                  {item.icon(selectedTab === item.label)}
                </Box>
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                className={selectedTab === item.label ? "font-semibold" : ""}
              />
              {selectedTab === item.label && (
                <Box className="w-2 h-2 bg-white rounded-full animate-pulse" />
              )}
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );

  const StatCard = ({ title, value, icon, color, subtitle, trend }) => {
    const getColorClasses = (color) => {
      const colorMap = {
        "bg-blue-600": {
          bg: "bg-gradient-to-r from-blue-500 to-blue-600",
          text: "text-blue-600",
          hoverText: "group-hover:text-blue-700",
          overlay: "bg-gradient-to-br from-blue-50 to-blue-100",
        },
        "bg-green-600": {
          bg: "bg-gradient-to-r from-green-500 to-green-600",
          text: "text-green-600",
          hoverText: "group-hover:text-green-700",
          overlay: "bg-gradient-to-br from-green-50 to-green-100",
        },
        "bg-green-500": {
          bg: "bg-gradient-to-r from-green-500 to-green-600",
          text: "text-green-600",
          hoverText: "group-hover:text-green-700",
          overlay: "bg-gradient-to-br from-green-50 to-green-100",
        },
        "bg-yellow-600": {
          bg: "bg-gradient-to-r from-yellow-500 to-yellow-600",
          text: "text-yellow-600",
          hoverText: "group-hover:text-yellow-700",
          overlay: "bg-gradient-to-br from-yellow-50 to-yellow-100",
        },
        "bg-blue-400": {
          bg: "bg-gradient-to-r from-blue-400 to-blue-500",
          text: "text-blue-600",
          hoverText: "group-hover:text-blue-700",
          overlay: "bg-gradient-to-br from-blue-50 to-blue-100",
        },
        "bg-purple-600": {
          bg: "bg-gradient-to-r from-purple-500 to-purple-600",
          text: "text-purple-600",
          hoverText: "group-hover:text-purple-700",
          overlay: "bg-gradient-to-br from-purple-50 to-purple-100",
        },
        "bg-teal-600": {
          bg: "bg-gradient-to-r from-teal-500 to-teal-600",
          text: "text-teal-600",
          hoverText: "group-hover:text-teal-700",
          overlay: "bg-gradient-to-br from-teal-50 to-teal-100",
        },
      };
      return colorMap[color] || colorMap["bg-blue-600"];
    };

    const colors = getColorClasses(color);

    return (
      <Card className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 hover:scale-105 cursor-pointer group stat-card border border-gray-100">
        <CardContent
          sx={{
            paddingBottom: "10px !important",
            paddingTop: "10px !important",
            paddingLeft: "10px !important",
            paddingRight: "10px !important",
          }}
          className="relative overflow-hidden"
        >
          {/* Background gradient overlay */}
          <div
            className={`absolute inset-0 ${colors.overlay} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}
          ></div>

          <Box className="relative z-10 flex items-center gap-3">
            <Box
              className={`p-2 ${colors.bg} rounded-lg shadow-sm group-hover:shadow-md transition-shadow duration-300`}
            >
              {React.cloneElement(icon, { className: "text-white text-sm" })}
            </Box>
            <Box className="flex-1">
              <Typography
                variant="body2"
                className="text-gray-600 group-hover:text-gray-800 transition-colors duration-300 font-medium text-xs"
              >
                {title}
              </Typography>
              {subtitle && (
                <Typography variant="caption" className="text-gray-500 text-xs">
                  {subtitle}
                </Typography>
              )}
            </Box>
            <Box className="text-right">
              <Typography
                variant="h6"
                className={`font-bold ${colors.text} ${colors.hoverText} transition-colors duration-300`}
              >
                {value}
              </Typography>
              {trend && (
                <Typography
                  variant="caption"
                  className={`text-${
                    trend > 0 ? "green" : "red"
                  }-600 text-xs font-medium`}
                >
                  {trend > 0 ? "+" : ""}
                  {trend}%
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const fetchPaymentReport = async () => {
    setPaymentLoading(true);
    try {
      const params = { payment_filter: paymentFilter, search: paymentSearch };
      if (paymentFilter === "monthly") {
        params.month = paymentMonth;
        params.year = paymentYear;
      }
      const endpoint =
        paymentType === "hostel"
          ? "hostel/admin/hostel-dashboard-stats/"
          : "library/admin/library-dashboard-stats/";
      const res = await makeAuthenticatedRequest(async (newConfig = config) =>
        axios.get(`${process.env.REACT_APP_API_URL}${endpoint}`, {
          ...newConfig,
          params,
        })
      );
      setPaymentReport(res.data.payment_report || null);
    } catch (error) {
      toast.error("Failed to load payment report");
    } finally {
      setPaymentLoading(false);
    }
  };

  useEffect(() => {
    if (selectedTab === "Payment Report") {
      fetchPaymentReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, paymentType, paymentFilter, paymentMonth, paymentYear]);

  const sendPaymentReminder = async (invoiceId) => {
    const confirmed = window.confirm("Send payment reminder for this unpaid invoice?");
    if (!confirmed) return;
    const loadingToast = toast.loading("Sending reminder...");
    try {
      const endpoint =
        paymentType === "hostel"
          ? `hostel/invoices/${invoiceId}/send-reminder/`
          : `library/invoices/${invoiceId}/send-reminder/`;
      await makeAuthenticatedRequest(async (newConfig = config) =>
        axios.post(`${process.env.REACT_APP_API_URL}${endpoint}`, {}, newConfig)
      );
      toast.dismiss(loadingToast);
      toast.success("Reminder sent and logged successfully");
      fetchPaymentReport();
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.response?.data?.error || "Reminder sending failed");
    }
  };

  const formatMoney = (value) => `₹${Number(value || 0).toFixed(2)}`;
  const formatDate = (value) =>
    value ? new Date(value).toLocaleDateString("en-IN") : "-";

  const renderBookingStatus = (bookingStatus) => {
    const normalizedStatus = (bookingStatus || "unknown").toLowerCase();
    const statusStyles = {
      approved: "bg-green-100 text-green-700",
      pending: "bg-yellow-100 text-yellow-700",
      cancelled: "bg-red-100 text-red-700",
      rejected: "bg-red-100 text-red-700",
      expired: "bg-gray-200 text-gray-700",
    };

    return (
      <span
        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${
          statusStyles[normalizedStatus] || "bg-gray-100 text-gray-700"
        }`}
      >
        {normalizedStatus}
      </span>
    );
  };

  const renderPaymentRows = (rows, isPaid) => (
    <div className="overflow-x-auto bg-white rounded-xl shadow-lg border border-gray-100">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-100 text-gray-700">
          <tr>
            <th className="p-3 text-left">ID</th>
            <th className="p-3 text-left">Photo</th>
            <th className="p-3 text-left">Student</th>
            <th className="p-3 text-left">{paymentType === "hostel" ? "Room / Bed" : "Seat No"}</th>
            <th className="p-3 text-left">Contact</th>
            <th className="p-3 text-left">Amount</th>
            <th className="p-3 text-left">Status</th>
            <th className="p-3 text-left">{isPaid ? "Paid On" : "Due Month"}</th>
            {!isPaid && <th className="p-3 text-left">Last Reminder</th>}
            {!isPaid && <th className="p-3 text-left">Count</th>}
            {!isPaid && <th className="p-3 text-left">Action</th>}
          </tr>
        </thead>
        <tbody>
          {(rows || []).length === 0 ? (
            <tr>
              <td colSpan={isPaid ? 8 : 11} className="p-6 text-center text-gray-500">
                No records found
              </td>
            </tr>
          ) : (
            rows.map((item) => (
              <tr key={item.id} className="border-t hover:bg-gray-50">
                <td className="p-3 font-semibold">{item.student?.id}</td>
                <td className="p-3">
                  <Avatar src={item.student?.photo || ""} sx={{ width: 36, height: 36 }}>
                    {(item.student?.name || "S").charAt(0)}
                  </Avatar>
                </td>
                <td className="p-3">
                  <div className="font-semibold text-gray-800">{item.student?.name}</div>
                  <div className="text-xs text-gray-500">{item.student?.username}</div>
                </td>
                <td className="p-3">
                  {paymentType === "hostel" ? `${item.room || "-"} / ${item.bed || "-"}` : item.seat || "-"}
                </td>
                <td className="p-3">{item.student?.phone || "-"}</td>
                <td className="p-3 font-bold text-green-700">{formatMoney(item.amount)}</td>
                <td className="p-3">{renderBookingStatus(item.status)}</td>
                <td className="p-3">{formatDate(isPaid ? item.paid_on : item.due_month)}</td>
                {!isPaid && <td className="p-3">{formatDate(item.last_reminder_sent)}</td>}
                {!isPaid && <td className="p-3">{item.reminder_count || 0}</td>}
                {!isPaid && (
                  <td className="p-3">
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => sendPaymentReminder(item.id)}
                      className="bg-gradient-to-r from-blue-600 to-blue-700"
                    >
                      Send Reminder
                    </Button>
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  const renderPaymentReport = () => {
    const summary = paymentReport?.summary || {};
    const months = Array.from({ length: 12 }, (_, i) => i + 1);
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 6 }, (_, i) => currentYear - i);

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-3 md:p-6 space-y-5">
        <div className="bg-white rounded-xl shadow-lg p-4 space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex gap-2">
              {["hostel", "library"].map((type) => (
                <button
                  key={type}
                  onClick={() => setPaymentType(type)}
                  className={`px-4 py-2 rounded-lg font-semibold transition ${
                    paymentType === type
                      ? "bg-blue-600 text-white shadow"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {type === "hostel" ? "Hostel" : "Library"}
                </button>
              ))}
            </div>

            <div className="flex flex-col md:flex-row gap-3">
              <select
                value={paymentFilter}
                onChange={(e) => setPaymentFilter(e.target.value)}
                className="border rounded-lg px-3 py-2 bg-white"
              >
                <option value="today">Today</option>
                <option value="monthly">Monthly</option>
              </select>
              {paymentFilter === "monthly" && (
                <>
                  <select
                    value={paymentMonth}
                    onChange={(e) => setPaymentMonth(e.target.value)}
                    className="border rounded-lg px-3 py-2 bg-white"
                  >
                    {months.map((m) => (
                      <option key={m} value={m}>
                        {new Date(2000, m - 1, 1).toLocaleString("default", { month: "long" })}
                      </option>
                    ))}
                  </select>
                  <select
                    value={paymentYear}
                    onChange={(e) => setPaymentYear(e.target.value)}
                    className="border rounded-lg px-3 py-2 bg-white"
                  >
                    {years.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </>
              )}
              <div className="flex gap-2">
                <input
                  value={paymentSearch}
                  onChange={(e) => setPaymentSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && fetchPaymentReport()}
                  placeholder="Search ID, name, phone, room, bed, seat"
                  className="border rounded-lg px-3 py-2 min-w-[260px]"
                />
                <Button variant="contained" onClick={fetchPaymentReport}>Search</Button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard title="Paid Students" value={summary.paid_count || 0} icon={<CheckCircleOutlineIcon />} color="bg-green-600" />
          <StatCard title="Unpaid Students" value={summary.unpaid_count || 0} icon={<HourglassEmptyIcon />} color="bg-yellow-600" />
          <StatCard title="Total Paid" value={formatMoney(summary.total_paid_amount)} icon={<CurrencyRupeeIcon />} color="bg-blue-600" />
          <StatCard title="Total Pending" value={formatMoney(summary.total_unpaid_amount)} icon={<CurrencyRupeeIcon />} color="bg-purple-600" />
        </div>

        {paymentLoading ? (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center text-gray-600">Loading payment report...</div>
        ) : (
          <div className="space-y-5">
            <div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">Paid Student List</h3>
              {renderPaymentRows(paymentReport?.paid_students, true)}
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">Unpaid Student List</h3>
              {renderPaymentRows(paymentReport?.unpaid_students, false)}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderDashboard = () => (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 md:p-4 lg:p-6">
      <div className="max-w-full mx-auto space-y-4 md:space-y-6">
        {/* Combined Statistics Row */}
        {(hostelStats || libraryStats) && (
          <div className="bg-white rounded-xl shadow-lg p-3 md:p-4 dashboard-card">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Hostel Statistics */}
              {hostelStats && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg">
                      <BusinessRoundedIcon className="text-white text-lg" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-800">
                        Hostel
                      </h3>
                      <p className="text-gray-500 text-xs">Booking analytics</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <StatCard
                      title="Total"
                      value={hostelStats.hostel.total_beds}
                      icon={<BusinessRoundedIcon />}
                      color="bg-blue-600"
                    />
                    <StatCard
                      title="Booked"
                      value={hostelStats.hostel.booked}
                      icon={<CheckCircleOutlineIcon />}
                      color="bg-green-600"
                    />
                    <StatCard
                      title="Pending"
                      value={hostelStats.hostel.pending}
                      icon={<HourglassEmptyIcon />}
                      color="bg-yellow-600"
                    />
                    <StatCard
                      title="Available"
                      value={hostelStats.hostel.available}
                      icon={<EventAvailableIcon />}
                      color="bg-blue-400"
                    />
                    <StatCard
                      title="Today"
                      value={hostelStats.trends.today_bookings}
                      icon={<CalendarTodayIcon />}
                      color="bg-purple-600"
                    />
                    <StatCard
                      title="Weekly"
                      value={hostelStats.trends.weekly_bookings}
                      icon={<TrendingUpIcon />}
                      color="bg-teal-600"
                    />
                  </div>
                </div>
              )}

              {/* Library Statistics */}
              {libraryStats && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-gradient-to-r from-green-500 to-green-600 rounded-lg">
                      <LocalLibraryIcon className="text-white text-lg" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-800">
                        Library
                      </h3>
                      <p className="text-gray-500 text-xs">Seat analytics</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <StatCard
                      title="Total"
                      value={libraryStats.library.total_seats}
                      icon={<LibraryBooksIcon />}
                      color="bg-green-600"
                    />
                    <StatCard
                      title="Booked"
                      value={libraryStats.library.booked}
                      icon={<CheckCircleOutlineIcon />}
                      color="bg-green-500"
                    />
                    <StatCard
                      title="Pending"
                      value={libraryStats.library.pending}
                      icon={<HourglassEmptyIcon />}
                      color="bg-yellow-600"
                    />
                    <StatCard
                      title="Available"
                      value={libraryStats.library.available}
                      icon={<EventAvailableIcon />}
                      color="bg-blue-400"
                    />
                    <StatCard
                      title="Today"
                      value={libraryStats.trends.today_bookings}
                      icon={<CalendarTodayIcon />}
                      color="bg-purple-600"
                    />
                    <StatCard
                      title="Weekly"
                      value={libraryStats.trends.weekly_bookings}
                      icon={<TrendingUpIcon />}
                      color="bg-teal-600"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Seat Maps Section */}
        <div className="bg-white rounded-xl shadow-lg p-3 md:p-4 lg:p-6 dashboard-card">
          <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
            <div className="p-2 md:p-3 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl">
              <BusinessRoundedIcon className="text-white text-lg md:text-2xl" />
            </div>
            <div>
              <h2 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-800">
                Live Availability Maps
              </h2>
              <p className="text-gray-600 text-xs md:text-sm">
                Real-time bed and seat availability
              </p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-2 md:p-4 mb-2 md:mb-4">
            <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-3 mb-2 md:mb-4">
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              sx={{
                "& .MuiTab-root": {
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  borderRadius: "8px",
                  marginRight: "4px",
                  minHeight: "40px",
                  padding: "6px 12px",
                  "@media (min-width: 640px)": {
                    fontSize: "1rem",
                    borderRadius: "12px",
                    marginRight: "8px",
                    minHeight: "48px",
                    padding: "8px 16px",
                  },
                },
                "& .Mui-selected": {
                  backgroundColor: "rgba(59, 130, 246, 0.1)",
                },
              }}
            >
              <Tab
                label={
                  <div className="flex items-center gap-1 md:gap-2">
                    <BusinessRoundedIcon className="text-sm md:text-base" />
                    <span className="hidden sm:inline">
                      Hostel Bed Availability
                    </span>
                    <span className="sm:hidden">Hostel</span>
                  </div>
                }
              />
              <Tab
                label={
                  <div className="flex items-center gap-1 md:gap-2">
                    <LocalLibraryIcon className="text-sm md:text-base" />
                    <span className="hidden sm:inline">
                      Library Seat Availability
                    </span>
                    <span className="sm:hidden">Library</span>
                  </div>
                }
              />
            </Tabs>
              <GlobalStudentSearch />
            </div>

            <div className="mt-2 md:mt-4">
              {activeTab === 0 && (
                <div className="bg-white rounded-lg p-2 md:p-4 shadow-sm overflow-hidden">
                  <RoomLibraryManager viewMode="room" />
                </div>
              )}
              {activeTab === 1 && (
                <div className="bg-white rounded-lg p-2 md:p-4 shadow-sm overflow-hidden">
                  <RoomLibraryManager viewMode="library" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDownloads = () => (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 md:p-6">
      <div className="max-w-full mx-auto">
        {/* Download Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Hostel Downloads */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-white/20 rounded-lg">
                  <BusinessRoundedIcon className="text-white text-2xl" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">Hostel Bookings</h2>
                  <p className="text-blue-100 text-sm">
                    Complete booking data export
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 text-sm font-bold">📊</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">
                      Data Format
                    </p>
                    <p className="text-xs text-gray-500">
                      CSV with detailed booking information
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 text-sm font-bold">📅</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">
                      Date Range
                    </p>
                    <p className="text-xs text-gray-500">
                      All historical booking data
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 text-sm font-bold">👥</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">
                      Student Details
                    </p>
                    <p className="text-xs text-gray-500">
                      Names, contact info, and preferences
                    </p>
                  </div>
                </div>
              </div>
              <Button
                variant="contained"
                onClick={downloadHostelCSV}
                className="w-full mt-6 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg"
                startIcon={<DownloadIcon />}
                sx={{ borderRadius: "12px", py: 1.5 }}
              >
                Download Hostel CSV
              </Button>
            </div>
          </div>

          {/* Library Downloads */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
            <div className="bg-gradient-to-r from-green-600 to-green-700 p-6 text-white">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-white/20 rounded-lg">
                  <LocalLibraryIcon className="text-white text-2xl" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">Library Bookings</h2>
                  <p className="text-green-100 text-sm">
                    Complete seat booking data export
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-600 text-sm font-bold">📚</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">
                      Data Format
                    </p>
                    <p className="text-xs text-gray-500">
                      CSV with detailed seat booking information
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-600 text-sm font-bold">🕒</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">
                      Time Slots
                    </p>
                    <p className="text-xs text-gray-500">
                      Booking times and durations
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-600 text-sm font-bold">📍</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">
                      Seat Information
                    </p>
                    <p className="text-xs text-gray-500">
                      Seat numbers and locations
                    </p>
                  </div>
                </div>
              </div>
              <Button
                variant="contained"
                onClick={downloadLibraryCSV}
                className="w-full mt-6 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 shadow-lg"
                startIcon={<DownloadIcon />}
                sx={{ borderRadius: "12px", py: 1.5 }}
              >
                Download Library CSV
              </Button>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-6 bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <span className="text-blue-600 text-2xl">💡</span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Download Tips
              </h3>
              <div className="space-y-2 text-sm text-gray-600">
                <p>
                  • CSV files can be opened in Excel, Google Sheets, or any
                  spreadsheet application
                </p>
                <p>
                  • Data includes booking dates, student information, and status
                  details
                </p>
                <p>
                  • Files are automatically named with the current date for easy
                  organization
                </p>
                <p>
                  • Large datasets may take a few moments to process and
                  download
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Box className="flex bg-gray-50 min-h-screen">
      {/* Mobile Menu Button */}
      <Box className="md:hidden fixed top-4 right-4 z-[30]">
        <IconButton
          onClick={() => setMobileOpen(true)}
          className="bg-white shadow-lg hover:bg-gray-50"
          size="large"
        >
          <MenuIcon />
        </IconButton>
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: 280,
            boxSizing: "border-box",
          },
          zIndex: 55,
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": {
            width: 280,
            boxSizing: "border-box",
          },
          zIndex: 10,
        }}
        open
      >
        {drawerContent}
      </Drawer>

      {/* Main Content */}
      <Box className="flex-1 md:ml-[280px] overflow-x-auto">
        {/* Header */}
        <Box className="bg-white shadow-lg border-b border-gray-200 sticky top-0 z-20">
          <Box className="flex items-center justify-between p-4 md:py-2">
            <Box className="flex items-center gap-3">
              <Box
                className={`p-3 rounded-xl shadow-lg ${
                  selectedTab === "Dashboard" &&
                  "bg-gradient-to-r from-blue-500 to-indigo-600"
                } ${
                  selectedTab === "Hostel History" &&
                  "bg-gradient-to-r from-blue-600 to-blue-700"
                } ${
                  selectedTab === "Library History" &&
                  "bg-gradient-to-r from-green-600 to-green-700"
                } ${
                  selectedTab === "View All Students" &&
                  "bg-gradient-to-r from-purple-600 to-purple-700"
                } ${
                  selectedTab === "Monthly Fee" &&
                  "bg-gradient-to-r from-blue-600 to-indigo-600"
                } ${
                  selectedTab === "Payment Report" &&
                  "bg-gradient-to-r from-green-600 to-emerald-700"
                } ${
                  selectedTab === "Revenue" &&
                  "bg-gradient-to-r from-emerald-600 to-teal-700"
                } ${
                  selectedTab === "Deposit Management" &&
                  "bg-gradient-to-r from-purple-600 to-indigo-700"
                } ${
                  selectedTab === "Blogs" &&
                  "bg-gradient-to-r from-blue-600 to-indigo-600"
                } ${
                  selectedTab === "Complaints" &&
                  "bg-gradient-to-r from-red-600 to-red-700"
                } ${
                  selectedTab === "Contacts" &&
                  "bg-gradient-to-r from-blue-600 to-indigo-700"
                } ${
                  selectedTab === "Downloads" &&
                  "bg-gradient-to-r from-blue-500 to-indigo-600"
                } ${
                  selectedTab === "Reviews" &&
                  "bg-gradient-to-r from-yellow-600 to-orange-700"
                } ${
                  selectedTab === "Switch Requests" &&
                  "bg-gradient-to-r from-red-600 to-red-700"
                }`}
              >
                {selectedTab === "Dashboard" && (
                  <Dashboard className="text-white text-2xl" />
                )}
                {selectedTab === "Hostel History" && (
                  <ApartmentOutlinedIcon className="text-white text-2xl" />
                )}
                {selectedTab === "Library History" && (
                  <LocalLibraryOutlinedIcon className="text-white text-2xl" />
                )}
                {selectedTab === "View All Students" && (
                  <PeopleIcon className="text-white text-2xl" />
                )}
                {selectedTab === "Monthly Fee" && (
                  <CurrencyRupeeIcon className="text-white text-2xl" />
                )}
                {selectedTab === "Payment Report" && (
                  <CurrencyRupeeIcon className="text-white text-2xl" />
                )}
                {selectedTab === "Revenue" && (
                  <TrendingUpIcon className="text-white text-2xl" />
                )}
                {selectedTab === "Deposit Management" && (
                  <AccountBalanceWalletIcon className="text-white text-2xl" />
                )}
                {selectedTab === "Blogs" && (
                  <ArticleIcon className="text-white text-2xl" />
                )}
                {selectedTab === "Complaints" && (
                  <ReportProblemIcon className="text-white text-2xl" />
                )}
                {selectedTab === "Contacts" && (
                  <MarkEmailUnreadOutlinedIcon className="text-white text-2xl" />
                )}
                {selectedTab === "Downloads" && (
                  <DownloadIcon className="text-white text-2xl" />
                )}
                {selectedTab === "Reviews" && (
                  <StarIcon className="text-white text-2xl" />
                )}
                {selectedTab === "Switch Requests" && (
                  <SwitchRequestIcon className="text-white text-2xl" />
                )}
              </Box>
              <Box>
                <span
                  // variant="h5"
                  className="mb-1 text-lg sm:text-3xl font-bold text-gray-800"
                >
                  {selectedTab}
                </span>
                <Typography sx={{ fontSize: "16px" }} className="text-gray-600">
                  {selectedTab === "Dashboard" && "Overview & Analytics"}
                  {selectedTab === "Hostel History" &&
                    "View and manage hostel booking records"}
                  {selectedTab === "Library History" &&
                    "View and manage library booking records"}
                  {selectedTab === "View All Students" &&
                    "Student database and profile management"}
                  {selectedTab === "Monthly Fee" &&
                    "Configure hostel and library fee structures"}
                  {selectedTab === "Payment Report" &&
                    "Paid, unpaid and reminder tracking"}
                  {selectedTab === "Revenue" &&
                    "Invoice-linked revenue tracking and analytics"}
                  {selectedTab === "Deposit Management" &&
                    "Security deposit tracking and returns"}
                  {selectedTab === "Blogs" && "Manage blog posts"}
                  {selectedTab === "Complaints" && "Manage complaints"}
                  {selectedTab === "Contacts" && "Manage form submissions"}
                  {selectedTab === "Downloads" &&
                    "Export booking data and reports"}
                  {selectedTab === "Reviews" &&
                    "Monitor and respond to student reviews"}
                  {selectedTab === "Switch Requests" &&
                    "View and manage switch requests"}
                </Typography>
              </Box>
            </Box>
            <Box className="flex items-center gap-2 md:gap-3">
              <Box className="hidden md:flex items-center gap-2 text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded-lg">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                <span>Last updated:</span>
                <span className="font-medium">
                  {new Date().toLocaleTimeString()}
                </span>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Content Area */}
        <Box style={{ maxHeight: "calc(100vh - 120px)" }} className="flex-1">
          {isLoading ? (
            <Box className="flex items-center justify-center h-64">
              <Box className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <Typography variant="h6" className="text-gray-600">
                  Loading dashboard...
                </Typography>
              </Box>
            </Box>
          ) : (
            <>
              {selectedTab === "Dashboard" && renderDashboard()}
              {selectedTab === "Hostel History" && <HostelBookingHistory />}
              {selectedTab === "Library History" && <LibraryBookingHistory />}
              {selectedTab === "View All Students" && <AdminStudentList />}
              {selectedTab === "Switch Requests" && <AdminSwitchRequests />}
              {selectedTab === "Monthly Fee" && <MonthlyFee />}
              {selectedTab === "Payment Report" && renderPaymentReport()}
              {selectedTab === "Revenue" && <RevenueManagement />}
              {selectedTab === "Deposit Management" && <DepositManagement />}
              {selectedTab === "Blogs" && <AdminBlogs />}
              {selectedTab === "Complaints" && <AdminComplaints />}
              {selectedTab === "Contacts" && <ContactUs />}
              {selectedTab === "Downloads" && renderDownloads()}
              {selectedTab === "Reviews" && <AdminReviews />}
            </>
          )}
        </Box>

        {/* Modals */}
        {/* The following modals are no longer used as selectedBookingId and selectedBedSeatId are removed */}
        {/* {selectedBookingId && (
              <BookingModal
                bookingId={selectedBookingId}
                type={selectedType}
                onClose={() => setSelectedBookingId(null)}
                onAction={() => window.location.reload()}
              />
        )} */}
        {/* {selectedBedSeatId && (
              <PendingBookingsModal
                bedSeatId={selectedBedSeatId}
                type={selectedType}
                onClose={() => setSelectedBedSeatId(null)}
              />
        )} */}
      </Box>
    </Box>
  );
};

export default AdminDashboard;
